import React, { useState } from 'react';
import { Header, Footer, SEOHead } from '../components';
import { useSEO } from '../hooks/useSEO';
import { pageConfigs } from '../utils/seoConfig';
import { sendConsultationForm } from '../utils/emailService';
import { 
  CalendarDaysIcon,
  ClockIcon,
  CheckIcon,
  PhoneIcon,
  VideoCameraIcon,
  ChatBubbleLeftRightIcon,
  UserIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  ArrowRightIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const ConsultationPage: React.FC = () => {
  useSEO(pageConfigs.consultation);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    consultationType: '',
    preferredDate: '',
    preferredTime: '',
    meetingType: 'video',
    challenges: '',
    goals: '',
    currentSituation: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const consultationTypes = [
    { value: 'lead-generation', label: 'Lead Generation Strategy' },
    { value: 'mobile-app', label: 'Mobile App Development' },
    { value: 'website', label: 'Website Development' },
    { value: 'digital-marketing', label: 'Digital Marketing' },
    { value: 'amazon-ads', label: 'Amazon Advertising' },
    { value: 'general', label: 'General Business Growth' }
  ];

  const timeSlots = [
    '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Full name is required';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email';
    }
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    }
    if (!formData.company.trim()) {
      errors.company = 'Company name is required';
    }
    if (!formData.consultationType) {
      errors.consultationType = 'Please select a consultation topic';
    }
    if (!formData.preferredDate) {
      errors.preferredDate = 'Please select a preferred date';
    }
    if (!formData.preferredTime) {
      errors.preferredTime = 'Please select a preferred time';
    }
    
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      await sendConsultationForm({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        consultationType: formData.consultationType,
        preferredDate: formData.preferredDate,
        preferredTime: formData.preferredTime,
        meetingType: formData.meetingType,
        challenges: formData.challenges,
        goals: formData.goals,
        currentSituation: formData.currentSituation
      });

      setSubmitMessage('Thank you! Your consultation has been scheduled. We\'ll send you a confirmation email shortly.');
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        consultationType: '',
        preferredDate: '',
        preferredTime: '',
        meetingType: 'video',
        challenges: '',
        goals: '',
        currentSituation: ''
      });
      setCurrentStep(1);
      setFormErrors({});

      setTimeout(() => setSubmitMessage(''), 5000);
    } catch (error) {
      setSubmitMessage('Error submitting form. Please try again or contact us directly.');
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.name && formData.email && formData.phone;
      case 2:
        return formData.consultationType && formData.preferredDate && formData.preferredTime;
      case 3:
        return true; // Optional fields
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead config={pageConfigs.consultation} />
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 section-padding relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800"></div>
        
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{animationDelay: '2s'}}></div>
        
        <div className="container-custom text-center relative z-10">
          <div className="inline-flex items-center space-x-2 bg-blue-500/20 px-4 py-2 rounded-full border border-blue-400/50 mb-8">
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
            <span className="text-blue-300 font-semibold text-sm">Free 30-Minute Consultation</span>
          </div>
          
          <h1 className="heading-section text-white mb-6">
            Schedule Your Free 
            <span className="bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent"> Strategy Consultation</span>
          </h1>
          
          <p className="text-body-lg text-slate-300 mb-12 max-w-3xl mx-auto">
            Get expert advice on your business challenges and discover opportunities for growth. 
            Our consultation is completely free with no obligations attached.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding bg-gradient-to-b from-white to-slate-50">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            <div className="group text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <ClockIcon className="w-10 h-10 text-white" />
              </div>
              <h3 className="heading-card text-slate-900 mb-3">30 Minutes</h3>
              <p className="text-slate-600">Focused discussion on your specific needs and challenges.</p>
            </div>
            <div className="group text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <CheckIcon className="w-10 h-10 text-white" />
              </div>
              <h3 className="heading-card text-slate-900 mb-3">100% Free</h3>
              <p className="text-slate-600">No cost, no obligations. Just valuable insights for your business.</p>
            </div>
            <div className="group text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <UserIcon className="w-10 h-10 text-white" />
              </div>
              <h3 className="heading-card text-slate-900 mb-3">Expert Advice</h3>
              <p className="text-slate-600">Get insights from our experienced team of specialists.</p>
            </div>
            <div className="group text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <ChatBubbleLeftRightIcon className="w-10 h-10 text-white" />
              </div>
              <h3 className="heading-card text-slate-900 mb-3">Actionable Plan</h3>
              <p className="text-slate-600">Leave with concrete next steps and recommendations.</p>
            </div>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* What We'll Cover */}
              <div>
                <h2 className="heading-card text-slate-900 mb-8 flex items-center space-x-3">
                  <SparklesIcon className="w-6 h-6 text-blue-600" />
                  <span>What We'll Cover</span>
                </h2>
                <div className="space-y-5">
                  <div className="flex items-start space-x-3">
                    <CheckIcon className="w-6 h-6 text-green-500 mt-1" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Current Situation Analysis</h4>
                      <p className="text-slate-600">Review your current marketing efforts and identify gaps.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckIcon className="w-6 h-6 text-green-500 mt-1" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Growth Opportunities</h4>
                      <p className="text-slate-600">Discover untapped opportunities in your market.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckIcon className="w-6 h-6 text-green-500 mt-1" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Strategic Recommendations</h4>
                      <p className="text-slate-600">Get specific advice tailored to your business goals.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckIcon className="w-6 h-6 text-green-500 mt-1" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Next Steps Plan</h4>
                      <p className="text-slate-600">Clear action items you can implement immediately.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckIcon className="w-6 h-6 text-green-500 mt-1" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Q&A Session</h4>
                      <p className="text-slate-600">Ask any questions about your specific challenges.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Booking Form */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-3xl blur-2xl"></div>
                <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/20">
                  <h3 className="heading-card text-slate-900 mb-8 flex items-center space-x-3">
                    <CalendarDaysIcon className="w-6 h-6 text-blue-600" />
                    <span>Book Your Consultation</span>
                  </h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Step 1: Contact Information */}
                  {currentStep === 1 && (
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Full Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 rounded-lg border-2 focus:outline-none transition-all duration-200 bg-slate-50 ${
                            formErrors.name ? 'border-red-500 focus:border-red-500' : 'border-slate-200 focus:border-blue-500'
                          }`}
                          placeholder="Your full name"
                        />
                        {formErrors.name && <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 rounded-lg border-2 focus:outline-none transition-all duration-200 bg-slate-50 ${
                            formErrors.email ? 'border-red-500 focus:border-red-500' : 'border-slate-200 focus:border-blue-500'
                          }`}
                          placeholder="your@email.com"
                        />
                        {formErrors.email && <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Phone Number *</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 rounded-lg border-2 focus:outline-none transition-all duration-200 bg-slate-50 ${
                            formErrors.phone ? 'border-red-500 focus:border-red-500' : 'border-slate-200 focus:border-blue-500'
                          }`}
                          placeholder="(123) 456-7890"
                        />
                        {formErrors.phone && <p className="text-red-500 text-sm mt-1">{formErrors.phone}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Company Name *</label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 rounded-lg border-2 focus:outline-none transition-all duration-200 bg-slate-50 ${
                            formErrors.company ? 'border-red-500 focus:border-red-500' : 'border-slate-200 focus:border-blue-500'
                          }`}
                          placeholder="Your company name"
                        />
                        {formErrors.company && <p className="text-red-500 text-sm mt-1">{formErrors.company}</p>}
                      </div>
                    </div>
                  )}

                  {/* Step 2: Schedule & Meeting Type */}
                  {currentStep === 2 && (
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Consultation Topic *</label>
                        <select
                          name="consultationType"
                          value={formData.consultationType}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 rounded-lg border-2 focus:outline-none transition-all duration-200 bg-slate-50 ${
                            formErrors.consultationType ? 'border-red-500 focus:border-red-500' : 'border-slate-200 focus:border-blue-500'
                          }`}
                        >
                          <option value="">Select consultation topic</option>
                          {consultationTypes.map((type) => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                        {formErrors.consultationType && <p className="text-red-500 text-sm mt-1">{formErrors.consultationType}</p>}
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-2">Preferred Date *</label>
                          <input
                            type="date"
                            name="preferredDate"
                            value={formData.preferredDate}
                            onChange={handleInputChange}
                            min={new Date().toISOString().split('T')[0]}
                            className={`w-full px-4 py-3 rounded-lg border-2 focus:outline-none transition-all duration-200 bg-slate-50 ${
                              formErrors.preferredDate ? 'border-red-500 focus:border-red-500' : 'border-slate-200 focus:border-blue-500'
                            }`}
                          />
                          {formErrors.preferredDate && <p className="text-red-500 text-sm mt-1">{formErrors.preferredDate}</p>}
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-2">Preferred Time *</label>
                          <select
                            name="preferredTime"
                            value={formData.preferredTime}
                            onChange={handleInputChange}
                            className={`w-full px-4 py-3 rounded-lg border-2 focus:outline-none transition-all duration-200 bg-slate-50 ${
                              formErrors.preferredTime ? 'border-red-500 focus:border-red-500' : 'border-slate-200 focus:border-blue-500'
                            }`}
                          >
                            <option value="">Select time</option>
                            {timeSlots.map((time) => (
                              <option key={time} value={time}>{time}</option>
                            ))}
                          </select>
                          {formErrors.preferredTime && <p className="text-red-500 text-sm mt-1">{formErrors.preferredTime}</p>}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-3">Meeting Type *</label>
                        <div className="grid grid-cols-3 gap-3">
                          <label className="cursor-pointer">
                            <input
                              type="radio"
                              name="meetingType"
                              value="video"
                              checked={formData.meetingType === 'video'}
                              onChange={handleInputChange}
                              className="sr-only"
                            />
                            <div className={`p-3 rounded-lg border-2 text-center transition-colors ${
                              formData.meetingType === 'video'
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-slate-200 bg-white hover:border-slate-300'
                            }`}>
                              <VideoCameraIcon className="w-6 h-6 mx-auto mb-2 text-slate-600" />
                              <span className="text-sm font-semibold">Video Call</span>
                            </div>
                          </label>
                          <label className="cursor-pointer">
                            <input
                              type="radio"
                              name="meetingType"
                              value="phone"
                              checked={formData.meetingType === 'phone'}
                              onChange={handleInputChange}
                              className="sr-only"
                            />
                            <div className={`p-3 rounded-lg border-2 text-center transition-colors ${
                              formData.meetingType === 'phone'
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-slate-200 bg-white hover:border-slate-300'
                            }`}>
                              <PhoneIcon className="w-6 h-6 mx-auto mb-2 text-slate-600" />
                              <span className="text-sm font-semibold">Phone Call</span>
                            </div>
                          </label>
                          <label className="cursor-pointer">
                            <input
                              type="radio"
                              name="meetingType"
                              value="in-person"
                              checked={formData.meetingType === 'in-person'}
                              onChange={handleInputChange}
                              className="sr-only"
                            />
                            <div className={`p-3 rounded-lg border-2 text-center transition-colors ${
                              formData.meetingType === 'in-person'
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-slate-200 bg-white hover:border-slate-300'
                            }`}>
                              <BuildingOfficeIcon className="w-6 h-6 mx-auto mb-2 text-slate-600" />
                              <span className="text-sm font-semibold">In Person</span>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 3: Additional Details */}
                  {currentStep === 3 && (
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Current Challenges</label>
                        <textarea
                          name="challenges"
                          value={formData.challenges}
                          onChange={handleInputChange}
                          rows={4}
                          className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 focus:border-blue-500 focus:outline-none transition-all duration-200 bg-slate-50"
                          placeholder="What challenges are you currently facing? (Optional)"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Goals & Objectives</label>
                        <textarea
                          name="goals"
                          value={formData.goals}
                          onChange={handleInputChange}
                          rows={4}
                          className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 focus:border-blue-500 focus:outline-none transition-all duration-200 bg-slate-50"
                          placeholder="What are you hoping to achieve? (Optional)"
                        />
                      </div>
                    </div>
                  )}

                  {/* Navigation Buttons */}
                  <div className="flex justify-between pt-6">
                    {currentStep > 1 && (
                      <button
                        type="button"
                        onClick={prevStep}
                        className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-3 px-6 rounded-xl border-2 border-slate-200 transition-all duration-300"
                      >
                        Previous
                      </button>
                    )}
                    
                    {currentStep < 3 ? (
                      <button
                        type="button"
                        onClick={nextStep}
                        disabled={!isStepValid()}
                        className={`ml-auto bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 flex items-center space-x-2 shadow-lg ${
                          !isStepValid() ? 'opacity-50 cursor-not-allowed hover:scale-100' : ''
                        }`}
                      >
                        <span>Next</span>
                        <ArrowRightIcon className="w-5 h-5" />
                      </button>
                    ) : (
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="ml-auto bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                      >
                        <CalendarDaysIcon className="w-5 h-5" />
                        <span>{isSubmitting ? 'Scheduling...' : 'Schedule My Free Consultation'}</span>
                      </button>
                    )}
                  </div>
                  {submitMessage && (
                    <div className={`mt-4 p-4 rounded-lg text-center font-semibold ${
                      submitMessage.includes('Error') 
                        ? 'bg-red-100 text-red-700' 
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {submitMessage}
                    </div>
                  )}
                </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-gradient-to-b from-white via-slate-50 to-white">
        <div className="container-custom">
          <div className="text-center mb-20">
            <h2 className="heading-section text-slate-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="subtitle text-slate-600">
              Hear from businesses that have benefited from our consultations
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative bg-white rounded-2xl p-8 border-2 border-slate-100 group-hover:border-blue-300 shadow-md group-hover:shadow-xl transition-all duration-300">
                <div className="flex items-center space-x-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-5 h-5 bg-gradient-to-r from-yellow-400 to-amber-400 rounded-full"></div>
                  ))}
                </div>
                <p className="text-slate-700 mb-6 italic leading-relaxed">
                  "The consultation was incredibly valuable. They identified opportunities we hadn't considered and gave us a clear roadmap to follow."
                </p>
                <div className="border-t border-slate-200 pt-4">
                  <div className="font-bold text-slate-900">Yaqoob Farhat Shami</div>
                  <div className="text-sm text-blue-600 font-semibold">United Arab Emirate .</div>
                </div>
              </div>
            </div>
            
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative bg-white rounded-2xl p-8 border-2 border-slate-100 group-hover:border-blue-300 shadow-md group-hover:shadow-xl transition-all duration-300">
                <div className="flex items-center space-x-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-5 h-5 bg-gradient-to-r from-yellow-400 to-amber-400 rounded-full"></div>
                  ))}
                </div>
                <p className="text-slate-700 mb-6 italic leading-relaxed">
                  "We hired 3Arrows Media for digital marketing and website development. And the results are awesome. We have been working for 3 years now."
                </p>
                <div className="border-t border-slate-200 pt-4">
                  <div className="font-bold text-slate-900">Nitin Jain</div>
                  <div className="text-sm text-blue-600 font-semibold">Founder, Esminis Essentials</div>
                </div>
              </div>
            </div>
            
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative bg-white rounded-2xl p-8 border-2 border-slate-100 group-hover:border-blue-300 shadow-md group-hover:shadow-xl transition-all duration-300">
                <div className="flex items-center space-x-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-5 h-5 bg-gradient-to-r from-yellow-400 to-amber-400 rounded-full"></div>
                  ))}
                </div>
                <p className="text-slate-700 mb-6 italic leading-relaxed">
                  "Professional, knowledgeable, and genuinely helpful. The consultation exceeded my expectations and provided real value."
                </p>
                <div className="border-t border-slate-200 pt-4">
                  <div className="font-bold text-slate-900">Bhavna joshi</div>
                  <div className="text-sm text-blue-600 font-semibold">Marketing Director</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default ConsultationPage;